import { motion } from 'motion/react';
import type { GetStaticProps, InferGetStaticPropsType } from 'next';
import { InstantSearch } from 'react-instantsearch';

import { HomePageDataDocument } from 'api';
import type { CustomUiState, RouteState } from 'components/SearchKit/constants';
import { initializeApollo } from 'lib/graphql/client';
import { createLogger } from 'lib/logging';

import { Container } from 'components/@tedui';
import {
  CustomConfigure,
  mainIndex,
  routing,
  searchClient
} from 'components/SearchKit';
import { Layout } from 'components/layout';
import TalkHead from 'components/pages/talks/TalkHead';

import { LazyLoadAd, TalkIndexAds } from 'components/ads';

import { FilterBar } from 'components/pages/talks/TalkIndex/FilterBar';
import {
  TopicsModal,
  TopicsModalProvider
} from 'components/pages/talks/TalkIndex/Modals';
import { SearchView } from 'components/pages/talks/TalkIndex/SearchView';
import { SearchErrorToast } from 'components/pages/talks/TalkIndex/SearchView/ErrorToast';

const apolloClient = initializeApollo();
const log = createLogger('Talks');

// Animation variants
const pageVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.6,
      staggerChildren: 0.15
    }
  }
};

const sectionVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.25, 0.4, 0.25, 1]
    }
  }
};

const layoutVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.25, 0.4, 0.25, 1],
      staggerChildren: 0.1
    }
  }
};

const Talks = ({
  talks
}: InferGetStaticPropsType<GetStaticProps>): React.ReactNode => {
  return (
    <>
      <TalkHead />

      <TopicsModalProvider>
        <Layout>
          <motion.div
            variants={pageVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={sectionVariants}>
              <LazyLoadAd showLoader={false}>
                <TalkIndexAds.TalkIndexAdBillboard />
              </LazyLoadAd>
            </motion.div>
            <motion.div
              className="relative z-10 mx-auto min-h-screen w-full max-w-full"
              variants={layoutVariants}
            >
              <InstantSearch
                searchClient={searchClient}
                indexName={mainIndex}
                routing={
                  routing as {
                    router: any;
                    stateMapping: {
                      stateToRoute(
                        uiState: Record<string, CustomUiState>
                      ): RouteState;
                      routeToState(
                        routeState: RouteState
                      ): Record<string, CustomUiState>;
                    };
                  }
                }
                future={{
                  preserveSharedStateOnUnmount: true
                }}
              >
                <SearchErrorToast />
                <CustomConfigure />
                <TopicsModal />
                <motion.div variants={sectionVariants}>
                  <FilterBar />
                </motion.div>
                <motion.div variants={sectionVariants}>
                  <Container className="flex flex-col">
                    <SearchView talks={talks} />
                  </Container>
                </motion.div>
              </InstantSearch>
              <motion.div variants={sectionVariants}>
                <LazyLoadAd>
                  <TalkIndexAds.TalkIndexAdInlineBTF />
                </LazyLoadAd>
              </motion.div>
            </motion.div>
          </motion.div>
        </Layout>
      </TopicsModalProvider>
    </>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  const { data, error: graphqlError } = await apolloClient.query({
    query: HomePageDataDocument
  });

  // Logging errors, but data fetching will silently fail and return empty sets
  if (graphqlError) {
    log.error('getting home page data failed', graphqlError);
  }
  const newestTalks = data?.newestTalks?.nodes?.filter(i => i !== null) || [];

  return {
    props: {
      talks: newestTalks
    },
    revalidate: 30
  };
};

export default Talks;
